import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import "@/components/radar-live-scope.css";
import { AdminDashboardBody } from "@/pages/Admin";
import BrandedHero from "@/components/BrandedHero";
import RadarHero from "@/components/RadarHero";
import WeatherWidget from "@/components/WeatherWidget";
import { useDesignTheme } from "@/hooks/useDesignTheme";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantCtx } from "@/lib/pro";
import { api } from "@/lib/api";

/**
 * AdminDashboardV2 — "Contractor Plus"-inspired dashboard, real data, zero deletes.
 *
 * ABOVE THE FOLD (new, matches design preview):
 *   - RadarHero map (signature — stays on top)
 *   - Branded hero (greeting + big stats in eyebrow)
 *   - Pipeline Arc (7-node WO journey, live counts)
 *   - Action Items (real jobs needing attention)
 *   - WO Status Donut + Quick-stat pills + Weather (right column)
 *   - Colored status flag strip
 *
 * BELOW THE FOLD:
 *   - <AdminDashboardBody v2/> — every existing V1 widget still there
 *
 * Escape hatch: `?v=classic` in URL rolls back to V1.
 */
export default function AdminDashboardV2() {
  const { user } = useAuth();
  const { tenant } = useTenantCtx();
  const { theme } = useDesignTheme();

  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [etaCounts, setEtaCounts] = useState({ open: 0, urgent: 0 });

  const reloadJobs = useCallback(() => {
    api.get("/jobs?limit=50").then(r => setJobs(r.data || [])).catch(() => {});
    api.get("/stats/admin").then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/stats/admin").then(r => setStats(r.data)).catch(() => {});
    api.get("/jobs?limit=50").then(r => setJobs(r.data || [])).catch(() => {});
    api.get("/eta-requests/counts").then(r => setEtaCounts(r.data || { open: 0, urgent: 0 })).catch(() => {});
  }, []);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "").split(" ")[0] || "there";
  const dateStr = now.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  // Pipeline nodes — real-data-driven. Each phase has a `to` deep-link that
  // filters the WO board by that status when the office taps the node.
  const pipeline = useMemo(() => {
    const s = stats || {};
    return [
      { label: "New",         count: s.new ?? 0,          color: "#8B93A7", done: true,  to: "/admin/jobs?status=new" },
      { label: "Pool",        count: s.pool ?? 0,         color: "#F5B93F", done: true,  to: "/admin/jobs?status=pool" },
      { label: "Assigned",    count: s.assigned ?? 0,     color: "#FF6A2B", done: true,  to: "/admin/jobs?status=assigned" },
      { label: "Active",      count: s.in_progress ?? 0,  color: "#B12FBB", done: true, active: true, to: "/admin/jobs?status=in_progress" },
      { label: "Completed",   count: s.completed ?? 0,    color: "#22C6A6", done: false, to: "/admin/jobs?status=completed" },
      { label: "Rebilled",    count: s.rebilled ?? s.paid_out ?? 0, color: "#3B82F6", done: false, to: "/admin/pmr-billing" },
      { label: "Paid",        count: s.paid ?? 0,         color: "#00C4CC", done: false, to: "/admin/jobs?status=paid" },
    ];
  }, [stats]);

  // Donut segments — real-data-driven
  const donut = useMemo(() => {
    const s = stats || {};
    const arr = [
      { label: "New",         color: "#8B93A7", n: s.new ?? 0 },
      { label: "Pool",        color: "#F5B93F", n: s.pool ?? 0 },
      { label: "Assigned",    color: "#FF6A2B", n: s.assigned ?? 0 },
      { label: "Active",      color: "#B12FBB", n: s.in_progress ?? 0 },
      { label: "Completed",   color: "#22C6A6", n: s.completed ?? 0 },
      { label: "Emergency",   color: "#E64C5A", n: s.habitability_open ?? s.habitability ?? 0 },
    ];
    const total = arr.reduce((a, x) => a + (x.n || 0), 0);
    return { segments: arr, total };
  }, [stats]);

  // Action items — synthesized from real signals
  const actionItems = useMemo(() => {
    const items = [];
    if (etaCounts?.open > 0) {
      items.push({
        icon: "⏱", color: "#E64C5A",
        text: `${etaCounts.open} ETA request${etaCounts.open === 1 ? "" : "s"} waiting`,
        meta: "PM tenants asking · answer now",
        to: "/admin/jobs?status=new",
      });
    }
    const needsAssign = jobs.filter(j => j.status === "new" || (!j.assigned_to_id && j.status !== "completed" && j.status !== "canceled")).slice(0, 3);
    needsAssign.forEach(j => {
      items.push({
        icon: "👷", color: "#F5B93F",
        text: `Assign · ${j.title || `WO#${j.work_order_number}`}`,
        meta: `${j.property_address || ""}${j.unit ? " · #" + j.unit : ""}`,
        to: `/admin/jobs/${j.id}`,
      });
    });
    const life = jobs.filter(j => j.priority === "urgent" || j.priority === "critical").slice(0, 2);
    life.forEach(j => {
      items.push({
        icon: "🔥", color: "#E64C5A",
        text: `Life-safety · ${j.title || `WO#${j.work_order_number}`}`,
        meta: `${j.property_address || ""} · priority ${j.priority}`,
        to: `/admin/jobs/${j.id}`,
      });
    });
    const readyClose = jobs.filter(j => j.status === "in_progress" && (j.photos_count > 0 || j.completed_photos_count > 0)).slice(0, 2);
    readyClose.forEach(j => {
      items.push({
        icon: "📸", color: "#3B82F6",
        text: `Photos in · ${j.title || `WO#${j.work_order_number}`}`,
        meta: "Ready to close",
        to: `/admin/jobs/${j.id}`,
      });
    });
    if (items.length === 0) {
      items.push({
        icon: "🎯", color: "#22C6A6",
        text: "You're all caught up",
        meta: "No urgent items right now",
        to: "/admin/jobs",
      });
    }
    return items.slice(0, 8);
  }, [jobs, etaCounts]);

  // Quick stats — real numbers
  const quickStats = useMemo(() => {
    const s = stats || {};
    return [
      { label: "Open WOs",    value: s.open ?? "—",       color: "#00C4CC", to: "/admin/jobs?status=open" },
      { label: "Pool",        value: s.pool ?? "—",       color: "#F5B93F", to: "/admin/jobs?status=pool" },
      { label: "Life Safety", value: s.habitability_open ?? s.habitability ?? "—", color: "#E64C5A", to: "/admin/jobs?status=habitability" },
      { label: "Completed",   value: s.completed ?? "—",  color: "#22C6A6", to: "/admin/jobs?status=completed" },
    ];
  }, [stats]);

  return (
    <AdminShell>
      {/* ── Radar map · stays at the very top (user request) ─── */}
      <div className="px-4 sm:px-8 pt-4 max-w-[1600px] mx-auto">
        <RadarHero/>
      </div>

      {/* ── Branded hero ─────────────────────────────────────── */}
      <BrandedHero
        eyebrow={tenant?.name || "Field Radar"}
        title={<>{greeting}, {firstName} 👋</>}
        subtitle="Command center for every work order, tenant, and dollar."
        size="md"
        chips={[
          { label: `${dateStr} · ${timeStr}` },
          { icon: "⚡", label: "Live" },
          stats?.open != null ? { label: `${stats.open} open` } : null,
          stats?.habitability_open ? { icon: "🔥", label: `${stats.habitability_open} life-safety` } : null,
        ].filter(Boolean)}
        actions={
          <>
            <Link to="/admin/jobs" data-testid="v2-hero-new-wo"
              className="h-9 px-4 rounded-full bg-white text-[#0F1F3A] text-[10px] font-black uppercase tracking-widest hover:bg-white/90 inline-flex items-center">
              + New WO
            </Link>
            <Link to="/admin?v=classic" data-testid="v2-hero-classic-toggle"
              title="Switch back to the classic dashboard"
              className="h-9 px-3 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/25 border border-white/20 inline-flex items-center">
              Use Classic
            </Link>
          </>
        }
      />

      {/* ── ABOVE THE FOLD ───────────────────────────────────── */}
      <div className="px-4 sm:px-8 max-w-[1600px] mx-auto -mt-14 sm:-mt-16 relative z-10 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* LEFT column — Pipeline Arc + Action Items */}
          <div className="lg:col-span-8 space-y-4">
            {/* Pipeline Arc */}
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 sm:p-6" data-testid="v2-pipeline">
              <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-2">
                <div>
                  <h3 className="text-lg sm:text-xl font-black text-[#0F1F3A]">Your Pipeline</h3>
                  <p className="text-[#8B93A7] text-xs font-medium">Where every WO is right now · click a stage to filter</p>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">
                  {stats?.total_jobs != null ? `${stats.total_jobs} total` : "—"}
                </span>
              </div>
              <PipelineArc phases={pipeline}/>
            </div>

            {/* Action Items + Today's Schedule — 2-col split so both stay glanceable */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Action Items */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 sm:p-6" data-testid="v2-action-items">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg sm:text-xl font-black text-[#0F1F3A]">Action Items</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{actionItems.length} · live</span>
                </div>
                <div className="space-y-1.5">
                  {actionItems.map((it, i) => (
                    <Link key={i} to={it.to} data-testid={`v2-action-${i}`}
                      className="flex items-center gap-3 p-2.5 hover:bg-slate-50 rounded-lg group transition-colors">
                      <div className="h-8 w-8 rounded-full flex items-center justify-center text-sm flex-shrink-0"
                        style={{ backgroundColor: it.color + "22" }}>
                        <span>{it.icon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-[#0F1F3A] truncate">{it.text}</div>
                        <div className="text-[10px] text-[#8B93A7] font-medium uppercase tracking-widest truncate">{it.meta}</div>
                      </div>
                      <span className="text-[#8B93A7] group-hover:text-[#0F1F3A] text-lg leading-none">→</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Today's Schedule mini-list */}
              <TodaysScheduleCard/>
            </div>

            {/* Move Pipeline — upcoming Move-Ins, Move-Outs, and Turns in a
                single scan-in-3-seconds row. Fills the space above Portfolio
                Pulse with property-manager gold. */}
            <MovePipelineCard/>

            {/* Landlord Kanban Preview — Phase-3 design sneak-peek. Real
                WO data bucketed into 4 lanes with tappable cards. Shows off
                the Contractor Plus lane aesthetic before the full board ships. */}
            <LandlordKanbanPreview jobs={jobs} onRefresh={reloadJobs}/>
          </div>

          {/* RIGHT column — Donut + Quick stats */}
          <div className="lg:col-span-4 space-y-4">
            {/* Live weather — Open-Meteo, tenant default location */}
            <WeatherWidget variant="card" tone="gradient" days={7} testid="v2-weather"/>

            {/* Status donut */}
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 sm:p-6" data-testid="v2-donut">
              <h3 className="text-lg sm:text-xl font-black text-[#0F1F3A] mb-2">WO Status</h3>
              <div className="flex items-center justify-center py-2">
                <StatusDonut segments={donut.segments} total={donut.total}/>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                {donut.segments.map(s => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: s.color }}/>
                    <span className="text-[#8B93A7] font-medium truncate">{s.label}</span>
                    <span className="font-black text-[#0F1F3A] ml-auto tabular-nums">{s.n}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick stats pills */}
            <div className="grid grid-cols-2 gap-3" data-testid="v2-quick-stats">
              {quickStats.map(s => (
                <Link key={s.label} to={s.to}
                  className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow border border-slate-100">
                  <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{s.label}</div>
                  <div className="text-2xl sm:text-3xl font-black mt-1 tabular-nums" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-[9px] font-bold text-[#8B93A7] mt-1">Tap to view →</div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Colored status flag strip */}
        <div className="mt-4 bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-3" data-testid="v2-status-strip">
          <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] mb-2 px-1">
            Portfolio pulse · scan status distribution
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {donut.segments.map(s => (
              <div key={s.label}
                className="flex-shrink-0 flex items-center gap-2 px-3 h-9 rounded-full border-2 hover:bg-slate-50 transition-colors cursor-pointer"
                style={{ borderColor: s.color + "44" }}>
                <span className="h-2 w-4 rounded-sm flex-shrink-0" style={{ background: s.color }}/>
                <span className="text-xs font-bold text-[#0F1F3A]">{s.label}</span>
                <span className="text-xs font-black" style={{ color: s.color }}>{s.n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── EXISTING V1 WIDGETS · unchanged, appear below ───── */}
      <div className="px-4 sm:px-8 max-w-[1600px] mx-auto pb-4">
        <details className="bg-white rounded-2xl border-2 border-dashed border-slate-200 group" data-testid="v2-more-widgets">
          <summary className="cursor-pointer px-4 py-3 text-[11px] font-black uppercase tracking-widest text-[#8B93A7] hover:text-[#0F1F3A] flex items-center gap-2 list-none">
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            <span>Show all classic widgets</span>
            <span className="ml-auto text-[9px] font-medium normal-case tracking-normal text-[#8B93A7]">
              Compliance · Morale · Guardrail · AppFolio · Kraaft · LiveFeed · Aging · Subs · Recent
            </span>
          </summary>
          <div className="border-t border-slate-100">
            <AdminDashboardBody v2/>
          </div>
        </details>
      </div>
    </AdminShell>
  );
}

/* ─── Sub-components ─────────────────────────────────────── */

function LandlordKanbanPreview({ jobs, onRefresh }) {
  // "Radar Live" — Phase-3 preview. 4-lane WO board rendered from real
  // jobs. Each card is draggable; drop it into another lane to reassign
  // or close the WO right from the dashboard. Dropping into "Ready to
  // Close" pops a confirm — everything else is one-tap.
  const [draggingId, setDraggingId] = useState(null);
  const [overLane, setOverLane] = useState(null);
  const [pendingClose, setPendingClose] = useState(null);  // {job, laneKey}
  const [busyId, setBusyId] = useState(null);
  // New-WO ping — increments a key to remount the ping element so the
  // 1.4s expanding wave animation restarts every time a fresh WO lands.
  const [pingKey, setPingKey] = useState(0);
  const lastSeenCountRef = useRef(null);

  // Detect newly-arrived WOs. Uses total open count (not `.length` of the
  // 50 loaded rows, since /api/jobs paginates). Falls back to array length
  // when totals aren't available. First render just seeds the baseline —
  // we never ping on initial load.
  useEffect(() => {
    if (!jobs) return;
    const openCount = (jobs || []).filter(j => j.status !== "completed" && j.status !== "canceled").length;
    const prev = lastSeenCountRef.current;
    lastSeenCountRef.current = openCount;
    // Only ping if we already had a baseline AND count went up
    if (prev != null && openCount > prev) {
      setPingKey(k => k + 1);
    }
  }, [jobs]);

  const lanes = useMemo(() => {
    const bucket = (matcher) => (jobs || []).filter(matcher);
    // Neon "radar scope" palette — vibrant so lanes pop against dark canvas.
    return [
      {
        key: "pool", label: "Unassigned", color: "#FBBF24",  // amber
        subtitle: "Waiting on a tech",
        rows: bucket(j => (j.status === "new" || j.status === "pool") || (!j.assigned_to_id && j.status !== "completed" && j.status !== "canceled")),
      },
      {
        key: "assigned", label: "Assigned", color: "#F97316",  // orange
        subtitle: "Truck picked, not on-site",
        rows: bucket(j => j.status === "assigned"),
      },
      {
        key: "active", label: "On-Site", color: "#22D3EE",  // cyan — the classic radar hue
        subtitle: "Clock-in active",
        rows: bucket(j => j.status === "in_progress"),
      },
      {
        key: "review", label: "Ready to Close", color: "#A855F7",  // violet
        subtitle: "Photos in · drop to close",
        rows: bucket(j => j.status === "in_progress" && (j.photos_count > 0 || j.completed_photos_count > 0)),
      },
    ].map(l => ({ ...l, rows: l.rows.slice(0, 3), total: l.rows.length }));
  }, [jobs]);

  const totalCards = lanes.reduce((a, l) => a + l.total, 0);

  const findJob = (id) => (jobs || []).find(j => j.id === id);

  // Given a target lane, compute the PATCH body. Returns null if drop
  // should be silently rejected, or {error: "..."} to show a toast.
  const buildPatchForLane = (job, laneKey) => {
    // Same lane as origin? no-op
    if (laneKey === "pool") {
      return { patch: { status: "pool", assigned_to: null } };
    }
    if (laneKey === "assigned") {
      if (!job.assigned_to_id && !job.assigned_to) {
        return { error: "Pick a tech from the WO page first — this lane needs an assignee." };
      }
      return { patch: { status: "assigned" } };
    }
    if (laneKey === "active") {
      return { patch: { status: "in_progress" } };
    }
    if (laneKey === "review") {
      // Handled separately with a confirm modal
      return { confirmClose: true };
    }
    return null;
  };

  // Snapshot the fields we care about so Undo can put things back. We
  // capture assigned_to (id) and status — that's the pair every drop
  // touches. If the WO had no assignee before, undo sends null too.
  const snapshotForUndo = (job) => ({
    status: job.status || "pool",
    assigned_to: job.assigned_to || job.assigned_to_id || null,
  });

  const applyPatch = async (jobId, patch, laneLabel, opts = {}) => {
    setBusyId(jobId);
    try {
      await api.patch(`/jobs/${jobId}`, patch);
      if (opts.undoFrom) {
        // Show a 5-second Undo chip. Clicking it PATCHes back to the
        // previous state without recursing (opts.undoFrom is undefined).
        toast.success(`Moved to ${laneLabel}`, {
          duration: 5000,
          action: {
            label: "Undo",
            onClick: () => applyPatch(jobId, opts.undoFrom, "previous state"),
          },
        });
      } else {
        toast.success(`Moved to ${laneLabel}`);
      }
      onRefresh && onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't move that WO";
      toast.error(String(msg));
    } finally {
      setBusyId(null);
    }
  };

  const onDrop = (e, lane) => {
    e.preventDefault();
    setOverLane(null);
    const id = e.dataTransfer.getData("text/plain") || draggingId;
    setDraggingId(null);
    if (!id) return;
    const job = findJob(id);
    if (!job) return;
    const decision = buildPatchForLane(job, lane.key);
    if (!decision) return;
    if (decision.error) { toast.error(decision.error); return; }
    if (decision.confirmClose) { setPendingClose({ job, laneLabel: lane.label }); return; }
    applyPatch(id, decision.patch, lane.label, { undoFrom: snapshotForUndo(job) });
  };

  const confirmClose = async () => {
    if (!pendingClose) return;
    const job = pendingClose.job;
    setPendingClose(null);
    await applyPatch(job.id, { status: "completed" }, "Completed", { undoFrom: snapshotForUndo(job) });
  };

  return (
    <div className="radar-live-scope rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(15,31,58,0.10)]" data-testid="v2-kanban-preview">
      {/* Animated V1-style sweeping cone — same easing as the login hero */}
      <div className="radar-live-sweep" aria-hidden="true"/>

      {/* One-shot cyan burst when a fresh WO lands. `key` remount restarts the CSS animation. */}
      {pingKey > 0 && (
        <div key={pingKey} className="radar-live-ping" aria-hidden="true" data-testid="v2-radar-ping"/>
      )}

      <div className="radar-live-body p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg sm:text-xl font-black text-white leading-none">Radar Live</h3>
              <span className="inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest bg-cyan-400/15 text-cyan-300 border border-cyan-400/40 px-2 py-0.5 rounded-full">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse"/>
                Scanning
              </span>
            </div>
            <p className="text-slate-400 text-xs font-medium truncate">
              Drag any WO between lanes to reassign · drop into "Ready to Close" to finish it
            </p>
          </div>
          <Link to="/admin/jobs-board" data-testid="v2-kanban-open-full"
            className="h-8 px-3 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 shadow-[0_0_20px_-4px_#22D3EE]">
            Open full board →
          </Link>
        </div>

        {totalCards === 0 ? (
          <div className="text-center py-8" data-testid="v2-kanban-empty">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 mb-2">📡</div>
            <div className="text-sm font-black text-white">Field is clear</div>
            <div className="text-[11px] text-slate-400 mt-1">New WOs will show up here as the scope picks them up.</div>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory -mx-1 px-1">
            {lanes.map(lane => {
              const isOver = overLane === lane.key;
              return (
                <div key={lane.key}
                  data-testid={`v2-kanban-lane-${lane.key}`}
                  onDragOver={(e) => { e.preventDefault(); setOverLane(lane.key); }}
                  onDragLeave={() => setOverLane(prev => prev === lane.key ? null : prev)}
                  onDrop={(e) => onDrop(e, lane)}
                  className="snap-start flex-shrink-0 w-[240px] sm:w-[260px] rounded-xl p-2.5 transition-all backdrop-blur-sm"
                  style={{
                    background: isOver
                      ? `linear-gradient(180deg, ${lane.color}22, ${lane.color}11)`
                      : "rgba(15, 23, 42, 0.55)",
                    border: `1px solid ${isOver ? lane.color : `${lane.color}55`}`,
                    boxShadow: isOver ? `0 0 0 2px ${lane.color}, 0 12px 32px -12px ${lane.color}` : "none",
                  }}>
                  <Link to={`/admin/jobs-board?lane=${lane.key}`}
                    data-testid={`v2-kanban-lane-header-${lane.key}`}
                    className="flex items-center gap-2 mb-2 group">
                    <span className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ background: lane.color, boxShadow: `0 0 8px ${lane.color}` }}/>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-black uppercase tracking-widest text-white truncate group-hover:underline">
                        {lane.label}
                      </div>
                      <div className="text-[9px] text-slate-400 font-medium truncate">{lane.subtitle}</div>
                    </div>
                    <span
                      className={`text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded-full flex-shrink-0 ${lane.total > 0 ? "radar-live-count-active" : ""}`}
                      style={{
                        background: lane.color,
                        color: "#0b1220",
                        "--lane": lane.color,
                      }}>
                      {lane.total}
                    </span>
                  </Link>
                  <div className="space-y-1.5">
                    {lane.rows.map(j => {
                      const isDragging = draggingId === j.id;
                      const isBusy = busyId === j.id;
                      return (
                        <div key={`${lane.key}-${j.id}`}
                          draggable={!isBusy}
                          onDragStart={(e) => {
                            e.dataTransfer.setData("text/plain", j.id);
                            e.dataTransfer.effectAllowed = "move";
                            setDraggingId(j.id);
                          }}
                          onDragEnd={() => { setDraggingId(null); setOverLane(null); }}
                          data-testid={`v2-kanban-card-${lane.key}-${j.id}`}
                          className={`radar-live-card block rounded-lg border p-2 group cursor-grab active:cursor-grabbing bg-slate-900/70 ${
                            isDragging ? "opacity-40 rotate-1" : ""
                          } ${isBusy ? "opacity-60 pointer-events-none" : ""}`}
                          style={{
                            borderColor: `${lane.color}55`,
                            "--lane": lane.color,
                          }}>
                          <Link to={`/admin/jobs/${j.id}`}
                            onClick={(e) => { if (isDragging) e.preventDefault(); }}
                            className="block"
                            draggable={false}
                            data-testid={`v2-kanban-card-link-${j.id}`}>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              {j.work_order_number && (
                                <span className="text-[9px] font-black tabular-nums text-slate-400">#{j.work_order_number}</span>
                              )}
                              {(j.priority === "urgent" || j.priority === "critical") && (
                                <span className="text-[8px] font-black uppercase tracking-widest text-red-200 bg-red-500/30 border border-red-400/50 px-1 py-0.5 rounded">
                                  {j.priority}
                                </span>
                              )}
                              {j.is_turn && (
                                <span className="text-[8px] font-black uppercase tracking-widest text-emerald-200 bg-emerald-500/25 border border-emerald-400/50 px-1 py-0.5 rounded">
                                  Turn
                                </span>
                              )}
                              {isBusy && (
                                <span className="text-[8px] font-black uppercase tracking-widest text-slate-400 ml-auto">Saving…</span>
                              )}
                            </div>
                            <div className="text-xs font-black text-white leading-tight line-clamp-2">
                              {j.title || "Untitled WO"}
                            </div>
                            <div className="mt-1 text-[10px] text-slate-400 font-medium truncate">
                              {j.property_address || "—"}
                              {j.unit && <span> · #{j.unit}</span>}
                            </div>
                            {j.assigned_to_name && (
                              <div className="mt-1 text-[9px] text-slate-400 font-black uppercase tracking-widest truncate">
                                👷 {j.assigned_to_name}
                              </div>
                            )}
                          </Link>
                        </div>
                      );
                    })}
                    {lane.total > lane.rows.length && (
                      <Link to={`/admin/jobs-board?lane=${lane.key}`}
                        data-testid={`v2-kanban-lane-more-${lane.key}`}
                        className="block text-center text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-white py-1">
                        +{lane.total - lane.rows.length} more →
                      </Link>
                    )}
                    {lane.total === 0 && (
                      <div className="text-center text-[10px] text-slate-500 font-medium py-3">
                        {isOver ? "Drop to move here" : "— No contacts —"}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Close confirm — dark to match the scope */}
        {pendingClose && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            data-testid="v2-kanban-close-confirm"
            onClick={() => setPendingClose(null)}>
            <div className="bg-slate-900 rounded-2xl shadow-2xl max-w-sm w-full p-5 border border-cyan-400/40"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-8 w-8 rounded-full bg-emerald-400/20 border border-emerald-400/50 text-emerald-300 flex items-center justify-center text-lg">✓</span>
                <h4 className="text-lg font-black text-white">Close this WO?</h4>
              </div>
              <p className="text-sm text-slate-300 font-medium mb-1">
                {pendingClose.job.title || "Untitled WO"}
              </p>
              <p className="text-xs text-slate-400 font-medium mb-4 truncate">
                {pendingClose.job.property_address || "—"}
                {pendingClose.job.unit && <span> · #{pendingClose.job.unit}</span>}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setPendingClose(null)}
                  data-testid="v2-kanban-close-cancel"
                  className="flex-1 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-black uppercase tracking-widest text-[10px] border border-slate-700">
                  Cancel
                </button>
                <button onClick={confirmClose}
                  data-testid="v2-kanban-close-confirm-btn"
                  className="flex-1 h-10 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black uppercase tracking-widest text-[10px] shadow-[0_0_20px_-4px_#10b981]">
                  Mark Completed
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PipelineArc({ phases }) {
  return (
    <div className="flex items-start justify-between gap-1 overflow-x-auto pb-1">
      {phases.map((p, i) => (
        <React.Fragment key={p.label}>
          <Link
            to={p.to || "#"}
            data-testid={`v2-pipeline-stage-${p.label.toLowerCase()}`}
            title={`${p.label} · ${p.count} — tap to filter`}
            className="flex flex-col items-center min-w-[72px] group hover:scale-[1.06] transition-transform">
            <div className={`h-11 w-11 sm:h-12 sm:w-12 rounded-full flex items-center justify-center relative group-hover:shadow-lg ${
              p.active ? "ring-4 ring-offset-2" : "ring-0 group-hover:ring-2 group-hover:ring-offset-1"
            }`} style={{ backgroundColor: p.color, ...(p.active ? { boxShadow: `0 0 0 4px ${p.color}22` } : {}) }}>
              <span className="text-white font-black text-sm tabular-nums">{p.count}</span>
              {p.done && !p.active && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#22C6A6] flex items-center justify-center text-white text-[9px] font-black">
                  ✓
                </span>
              )}
            </div>
            <div className="mt-2 text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-center group-hover:text-[#0F1F3A]"
              style={{ color: p.active ? p.color : "#8B93A7" }}>
              {p.label}
            </div>
          </Link>
          {i < phases.length - 1 && (
            <div className="flex-1 h-0.5 mt-5 sm:mt-6 rounded-full min-w-[12px]"
              style={{ background: p.done ? `linear-gradient(to right, ${p.color}, ${phases[i + 1].color})` : "#e5e7eb" }}/>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function StatusDonut({ segments, total }) {
  const totalN = Math.max(total, 1);
  const r = 60, C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width="180" height="180" viewBox="-90 -90 180 180" className="-rotate-90">
      <circle r={r} fill="none" stroke="#f1f5f9" strokeWidth="18"/>
      {segments.map((d, i) => {
        const pct = (d.n || 0) / totalN;
        const len = pct * C;
        const el = (
          <circle key={i} r={r} fill="none" stroke={d.color} strokeWidth="18"
            strokeDasharray={`${len} ${C}`} strokeDashoffset={-offset}/>
        );
        offset += len;
        return el;
      })}
      <text x="0" y="0" textAnchor="middle" dy="0.35em" className="rotate-90" transform="rotate(90)"
        fill="#0F1F3A" fontSize="24" fontWeight="900">
        {total}
      </text>
    </svg>
  );
}

/**
 * TodaysScheduleCard — mini list of WOs pinned to the current sweeper-radar
 * date. Clickable rows deep-link into the V2 job detail page. Grouped by
 * truck; sorted by route_order within each truck. Empty state points to
 * Sweeper Radar so the office can pin their crew's day.
 */
function TodaysScheduleCard() {
  const [pins, setPins] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const todayIso = React.useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get(`/jobs/sweeper-pins?date=${todayIso}`)
        .then(r => {
          if (!alive) return;
          setPins(Array.isArray(r.data) ? r.data : []);
          setRefreshedAt(new Date());
        })
        .catch(() => { if (alive) setPins(prev => prev ?? []); });
    load();
    // Poll every 30s so the card updates the second a tech finishes a stop.
    // Pause polling when the tab is hidden to save API calls; kick a fresh
    // load the moment focus comes back.
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 30000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [todayIso]);

  const grouped = React.useMemo(() => {
    if (!pins) return [];
    const map = {};
    for (const p of pins) {
      const key = p.truck_key || "_none";
      if (!map[key]) map[key] = { key, label: p.truck_label || "Unassigned", items: [] };
      map[key].items.push(p);
    }
    return Object.values(map);
  }, [pins]);

  const total = pins?.length || 0;
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  return (
    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 sm:p-6" data-testid="v2-today-schedule">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg sm:text-xl font-black text-[#0F1F3A]">Today's Schedule</h3>
          <p className="text-[#8B93A7] text-xs font-medium">{dateStr} · click any WO to open</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]" data-testid="v2-today-schedule-count">
            {total} pinned
          </span>
          {refreshedAt && (
            <span
              key={refreshedAt.getTime()}
              data-testid="v2-today-schedule-live-dot"
              title={`Refreshed ${refreshedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" })}`}
              className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"/>
          )}
          <Link to="/admin/lineup" data-testid="v2-today-schedule-radar"
            className="text-[9px] font-black uppercase tracking-widest text-[#00C4CC] hover:text-[#0F1F3A]">
            Radar →
          </Link>
        </div>
      </div>

      {pins === null && (
        <div className="space-y-1.5" data-testid="v2-today-schedule-loading">
          {[0, 1, 2].map(i => <div key={i} className="h-11 rounded-lg bg-slate-100 animate-pulse"/>)}
        </div>
      )}

      {pins !== null && total === 0 && (
        <div className="text-center py-6" data-testid="v2-today-schedule-empty">
          <div className="text-3xl mb-1">🌤️</div>
          <div className="text-sm font-black text-[#0F1F3A]">Nothing on the board yet</div>
          <div className="text-[11px] text-[#8B93A7] mt-1">Pin WOs on Sweeper Radar and they show up here.</div>
          <Link to="/admin/lineup" data-testid="v2-today-schedule-empty-cta"
            className="mt-3 inline-flex items-center h-8 px-3 rounded-full bg-[#00C4CC] hover:bg-[#00a8af] text-white text-[10px] font-black uppercase tracking-widest">
            Open Sweeper Radar
          </Link>
        </div>
      )}

      {pins !== null && total > 0 && (
        <div className="space-y-3 max-h-[280px] overflow-y-auto">
          {grouped.map(g => (
            <div key={g.key} data-testid={`v2-today-truck-${g.key}`}>
              <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] px-1 mb-1 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00C4CC]"/>
                {g.label}
                <span className="text-[#8B93A7]/60">· {g.items.length}</span>
              </div>
              <div className="space-y-1">
                {g.items.map((j) => {
                  const priTone = (j.priority === "critical" || j.priority === "urgent" || j.priority === "high")
                    ? "border-red-200 bg-red-50/60"
                    : j.is_turn ? "border-emerald-200 bg-emerald-50/60"
                    : "border-slate-200 bg-slate-50/60";
                  return (
                    <Link key={j.id} to={`/admin/jobs/${j.id}`}
                      data-testid={`v2-today-job-${j.id}`}
                      className={`flex items-center gap-2 p-2 rounded-lg border ${priTone} hover:shadow-sm hover:border-cyan-300 transition-all group`}>
                      <span className="h-6 w-6 rounded-md bg-white border border-slate-200 text-[10px] font-black text-slate-700 flex items-center justify-center flex-shrink-0 tabular-nums">
                        {j.route_order != null ? j.route_order + 1 : "—"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-black text-[#0F1F3A] truncate">
                          {j.property_address || "—"}
                          {j.unit && <span className="text-[#8B93A7] font-medium"> · {j.unit}</span>}
                        </div>
                        <div className="text-[10px] text-[#8B93A7] font-medium truncate">
                          {j.work_order_number ? `#${j.work_order_number} · ` : ""}{j.title || "No title"}
                        </div>
                      </div>
                      {j.is_turn && (
                        <span className="text-[8px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded flex-shrink-0">Turn</span>
                      )}
                      <span className="text-[#8B93A7] group-hover:text-[#0F1F3A] text-sm leading-none flex-shrink-0">→</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * MovePipelineCard — 3-lane "what's rolling next 30 days" widget.
 *
 * Lane 1: Move-Outs      → GET /api/move-radar/events?kind=move_out&status=open
 * Lane 2: Move-Ins       → GET /api/move-radar/events?kind=move_in&status=open
 * Lane 3: Mini Turns Board → GET /api/turns/board (open + in_progress counts,
 *         plus top-3 addresses in each state)
 *
 * All requests fire in parallel on mount. Each lane is clickable to jump into
 * its full page. Rows deep-link to the specific event/WO.
 */
function MovePipelineCard() {
  const [moveOuts, setMoveOuts] = useState(null);
  const [moveIns, setMoveIns] = useState(null);
  const [turns, setTurns] = useState(null);

  useEffect(() => {
    let alive = true;
    const gotoErr = () => (alive ? [] : []);
    Promise.all([
      api.get("/move-radar/events?kind=move_out&status=open&limit=5").then(r => r.data?.events || []).catch(gotoErr),
      api.get("/move-radar/events?kind=move_in&status=open&limit=5").then(r => r.data?.events || []).catch(gotoErr),
      api.get("/turns/board").then(r => r.data || { open: [], in_progress: [] }).catch(() => ({ open: [], in_progress: [] })),
    ]).then(([mo, mi, tb]) => {
      if (!alive) return;
      setMoveOuts(mo); setMoveIns(mi); setTurns(tb);
    });
    return () => { alive = false; };
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="v2-move-pipeline">
      <MoveLane
        title="Move-Outs"
        subtitle="Next 30 days"
        icon="📤"
        tone="amber"
        events={moveOuts}
        emptyLabel="No move-outs on the radar"
        countLink="/admin/move-radar?kind=move_out"
        rowLink={(e) => `/admin/move-radar?event=${e.id}`}
        testidPrefix="v2-moveout"
      />
      <MoveLane
        title="Move-Ins"
        subtitle="Next 30 days"
        icon="📥"
        tone="cyan"
        events={moveIns}
        emptyLabel="No move-ins on the radar"
        countLink="/admin/move-radar?kind=move_in"
        rowLink={(e) => `/admin/move-radar?event=${e.id}`}
        testidPrefix="v2-movein"
      />
      <MiniTurnsLane turns={turns}/>
    </div>
  );
}

const TONE_MAP = {
  amber: { chip: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500", accent: "text-amber-700" },
  cyan:  { chip: "bg-cyan-50 text-cyan-800 border-cyan-200",   dot: "bg-cyan-500",  accent: "text-cyan-700" },
  emerald: { chip: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500", accent: "text-emerald-700" },
};

function LaneHeader({ title, subtitle, icon, tone, count, countLink, testidPrefix }) {
  const t = TONE_MAP[tone] || TONE_MAP.cyan;
  return (
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center text-sm border ${t.chip}`}>
          {icon}
        </span>
        <div className="min-w-0">
          <h3 className="text-sm sm:text-base font-black text-[#0F1F3A] truncate">{title}</h3>
          <p className="text-[10px] text-[#8B93A7] font-medium truncate">{subtitle}</p>
        </div>
      </div>
      <Link to={countLink} data-testid={`${testidPrefix}-count-link`}
        className={`text-2xl font-black tabular-nums hover:opacity-80 ${t.accent}`}>
        {count == null ? "—" : count}
      </Link>
    </div>
  );
}

function daysUntil(iso) {
  try {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const diff = Math.round((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    return diff;
  } catch { return null; }
}

function MoveLane({ title, subtitle, icon, tone, events, emptyLabel, countLink, rowLink, testidPrefix }) {
  const total = events?.length ?? null;
  const t = TONE_MAP[tone] || TONE_MAP.cyan;
  return (
    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 sm:p-5" data-testid={testidPrefix}>
      <LaneHeader title={title} subtitle={subtitle} icon={icon} tone={tone}
        count={total} countLink={countLink} testidPrefix={testidPrefix}/>
      {events === null && (
        <div className="space-y-1.5" data-testid={`${testidPrefix}-loading`}>
          {[0, 1, 2].map(i => <div key={i} className="h-10 rounded-lg bg-slate-100 animate-pulse"/>)}
        </div>
      )}
      {events !== null && events.length === 0 && (
        <div className="text-center py-5" data-testid={`${testidPrefix}-empty`}>
          <div className="text-[11px] text-[#8B93A7] font-medium">{emptyLabel}</div>
        </div>
      )}
      {events !== null && events.length > 0 && (
        <div className="space-y-1">
          {events.slice(0, 4).map(e => {
            const dU = daysUntil(e.desired_date);
            const urgencyTone =
              dU == null ? "text-slate-400" :
              dU < 0 ? "text-red-600" :
              dU <= 3 ? "text-amber-600" :
              dU <= 14 ? t.accent :
              "text-slate-500";
            const urgencyLabel =
              dU == null ? "—" :
              dU < 0 ? `${Math.abs(dU)}d late` :
              dU === 0 ? "today" :
              dU === 1 ? "tomorrow" :
              `${dU}d out`;
            return (
              <Link key={e.id} to={rowLink(e)}
                data-testid={`${testidPrefix}-row-${e.id}`}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 group transition-colors">
                <span className={`h-1.5 w-1.5 rounded-full ${t.dot} flex-shrink-0`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black text-[#0F1F3A] truncate">
                    {e.address || "—"}
                    {e.unit && <span className="text-[#8B93A7] font-medium"> · {e.unit}</span>}
                  </div>
                  <div className="text-[10px] text-[#8B93A7] font-medium truncate">
                    {e.tenant_name || "Tenant TBD"}
                    {e.desired_date && <> · {e.desired_date}</>}
                  </div>
                </div>
                <span className={`text-[9px] font-black uppercase tracking-widest tabular-nums flex-shrink-0 ${urgencyTone}`}>
                  {urgencyLabel}
                </span>
              </Link>
            );
          })}
          {events.length > 4 && (
            <Link to={countLink} data-testid={`${testidPrefix}-more`}
              className={`block text-center text-[10px] font-black uppercase tracking-widest pt-1 ${t.accent} hover:opacity-70`}>
              + {events.length - 4} more →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function MiniTurnsLane({ turns }) {
  const t = TONE_MAP.emerald;
  const openCount = turns?.open?.length ?? null;
  const inProgCount = turns?.in_progress?.length ?? null;
  const awaiting = (turns?.in_progress || []).filter(j => j.turn_awaiting_release || j.turn_finished_at);
  const activeInProg = (turns?.in_progress || []).filter(j => !(j.turn_awaiting_release || j.turn_finished_at));
  const totalPool = (openCount ?? 0) + (inProgCount ?? 0);
  // Top 3 addresses across both states, prioritizing awaiting-release
  const topRows = [
    ...awaiting.slice(0, 3).map(j => ({ ...j, _state: "release" })),
    ...activeInProg.slice(0, 2).map(j => ({ ...j, _state: "in_prog" })),
    ...(turns?.open || []).slice(0, 2).map(j => ({ ...j, _state: "open" })),
  ].slice(0, 4);

  return (
    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 sm:p-5" data-testid="v2-mini-turns">
      <LaneHeader title="Turns Board" subtitle="Vacant unit pool" icon="🔁"
        tone="emerald" count={turns === null ? null : totalPool}
        countLink="/admin/turns-board" testidPrefix="v2-mini-turns"/>
      {turns === null && (
        <div className="space-y-1.5" data-testid="v2-mini-turns-loading">
          {[0, 1, 2].map(i => <div key={i} className="h-10 rounded-lg bg-slate-100 animate-pulse"/>)}
        </div>
      )}
      {turns !== null && (
        <>
          {/* 3-count strip */}
          <div className="grid grid-cols-3 gap-1.5 mb-2">
            <MiniStat label="Open" value={openCount} accent="text-cyan-700"/>
            <MiniStat label="In Prog" value={activeInProg.length} accent="text-fuchsia-700"/>
            <MiniStat label="Release" value={awaiting.length} accent="text-emerald-700" pulse={awaiting.length > 0}/>
          </div>
          {topRows.length === 0 ? (
            <div className="text-center py-3" data-testid="v2-mini-turns-empty">
              <div className="text-[11px] text-[#8B93A7] font-medium">Nothing in the turn pool</div>
            </div>
          ) : (
            <div className="space-y-1">
              {topRows.map(j => {
                const badgeClass =
                  j._state === "release" ? "bg-emerald-100 text-emerald-800" :
                  j._state === "in_prog" ? "bg-fuchsia-100 text-fuchsia-800" :
                  "bg-cyan-100 text-cyan-800";
                const badgeText = j._state === "release" ? "Release" : j._state === "in_prog" ? "In Prog" : "Open";
                return (
                  <Link key={`${j._state}-${j.id}`} to={`/admin/jobs/${j.id}`}
                    data-testid={`v2-mini-turns-row-${j._state}-${j.id}`}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 group transition-colors">
                    <span className={`h-1.5 w-1.5 rounded-full ${t.dot} flex-shrink-0`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-black text-[#0F1F3A] truncate">{j.property_address || "—"}</div>
                      <div className="text-[10px] text-[#8B93A7] font-medium truncate">
                        {j.turn_started_by_name ? `Started by ${j.turn_started_by_name}` : "Awaiting claim"}
                      </div>
                    </div>
                    <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${badgeClass}`}>
                      {badgeText}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent, pulse }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 py-1.5 text-center">
      <div className={`text-lg font-black tabular-nums ${accent}`}>
        {value ?? "—"}
        {pulse && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse align-middle"/>}
      </div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}

